import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useElectoralMetadataFields,
  useCreateElectoralMetadataField,
  useUpdateElectoralMetadataField,
  useDeleteElectoralMetadataField,
  useReorderElectoralMetadataFields
} from "../api/electoral-metadata/hooks";
import type { ElectoralMetadataFieldListItem } from "../api/electoral-metadata/types";
import { showError, confirmAction } from "../shared/toast";
import "./metadata-fields-page.css";

type FieldType = "Decimal" | "Text";

export const MetadataFieldsPage = (): JSX.Element => {
  const [editingField, setEditingField] =
    useState<ElectoralMetadataFieldListItem | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data: fields = [], isLoading, error } = useElectoralMetadataFields();
  const createMutation = useCreateElectoralMetadataField();
  const updateMutation = useUpdateElectoralMetadataField();
  const deleteMutation = useDeleteElectoralMetadataField();
  const reorderMutation = useReorderElectoralMetadataFields();

  const handleAdd = () => {
    setShowCreateForm(true);
    setEditingField(null);
  };

  const handleEdit = (field: ElectoralMetadataFieldListItem) => {
    setEditingField(field);
    setShowCreateForm(false);
  };

  const handleDelete = async (field: ElectoralMetadataFieldListItem) => {
    const confirmed = await confirmAction(
      `Är du säker på att du vill ta bort fältet "${field.name}"?`
    );
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(field.id);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? "Kunde inte ta bort fältet.";
      showError("Kunde inte ta bort fältet", msg);
    }
  };

  const handleCreateSubmit = async (
    name: string,
    type: FieldType,
    importCode: string | null
  ): Promise<void> => {
    if (!name.trim()) {
      showError("Ogiltig indata", "Namn krävs.");
      return;
    }
    if (type !== "Decimal" && type !== "Text") {
      showError("Ogiltig indata", "Typ måste vara Decimal eller Text.");
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        importCode: importCode?.trim() || null,
        type
      });
      setShowCreateForm(false);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string }; status?: number } })
        ?.response?.data;
      const msg = data?.error ?? "Kunde inte skapa fältet.";
      showError("Kunde inte skapa fältet", msg);
    }
  };

  const handleUpdateSubmit = async (
    name: string,
    type: FieldType,
    importCode: string | null
  ): Promise<void> => {
    if (!editingField) return;
    if (!name.trim()) {
      showError("Ogiltig indata", "Namn krävs.");
      return;
    }
    if (type !== "Decimal" && type !== "Text") {
      showError("Ogiltig indata", "Typ måste vara Decimal eller Text.");
      return;
    }
    try {
      await updateMutation.mutateAsync({
        fieldId: editingField.id,
        request: {
          name: name.trim(),
          importCode: importCode?.trim() || null,
          type
        }
      });
      setEditingField(null);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string } } })
        ?.response?.data;
      const msg = data?.error ?? "Kunde inte uppdatera fältet.";
      showError("Kunde inte uppdatera fältet", msg);
    }
  };

  const handleFormCancel = () => {
    setShowCreateForm(false);
    setEditingField(null);
  };

  const moveField = (index: number, direction: "up" | "down") => {
    const newOrder = fields.map((f) => f.id);
    const swap = direction === "up" ? index - 1 : index + 1;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[index], newOrder[swap]] = [newOrder[swap], newOrder[index]];
    reorderMutation.mutate(newOrder);
  };

  if (error) {
    return (
      <section className="metadata-fields-page" aria-label="Metadatafält">
        <h2>Metadatafält</h2>
        <p className="metadata-fields-page__error">Kunde inte ladda fält.</p>
      </section>
    );
  }

  return (
    <section className="metadata-fields-page" aria-label="Metadatafält">
      <div className="metadata-fields-page__header">
        <h2>Metadatafält</h2>
        <p className="metadata-fields-page__description">
          Hantera fältdefinitioner för valdistriktsmetadata (decimal eller text).
          Kolumnerna används i metadata-gridden och vid CSV-import.{" "}
          <Link to="/admin/metadata-grid">Tillbaka till valdistriktsmetadata</Link>.
        </p>
        <button
          type="button"
          className="button button--primary"
          onClick={handleAdd}
          aria-label="Lägg till fält"
        >
          + Lägg till fält
        </button>
      </div>

      {(showCreateForm || editingField) && (
        <div className="metadata-fields-page__form">
          <MetadataFieldForm
            field={editingField ?? undefined}
            onSave={
              editingField
                ? (name, type, importCode) => handleUpdateSubmit(name, type, importCode)
                : (name, type, importCode) => handleCreateSubmit(name, type, importCode)
            }
            onCancel={handleFormCancel}
            isSaving={
              createMutation.isPending || updateMutation.isPending
            }
          />
        </div>
      )}

      {isLoading ? (
        <p>Laddar...</p>
      ) : (
        <div className="metadata-fields-page__list">
          {fields.length === 0 && !showCreateForm ? (
            <p>Inga fält. Lägg till ett fält ovan.</p>
          ) : (
            <table className="metadata-fields-table">
              <thead>
                <tr>
                  <th>Namn</th>
                  <th>Importkod (CSV)</th>
                  <th>Typ</th>
                  <th aria-label="Ordning">Ordning</th>
                  <th aria-hidden>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f, index) => (
                  <tr key={f.id}>
                    <td>{f.name}</td>
                    <td>{f.importCode ?? "—"}</td>
                    <td>{f.type}</td>
                    <td>
                      <span className="metadata-fields-table__order-actions">
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() => moveField(index, "up")}
                          disabled={index === 0 || reorderMutation.isPending}
                          aria-label={`Flytta upp ${f.name}`}
                          title="Flytta upp"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() => moveField(index, "down")}
                          disabled={index === fields.length - 1 || reorderMutation.isPending}
                          aria-label={`Flytta ner ${f.name}`}
                          title="Flytta ner"
                        >
                          ↓
                        </button>
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => handleEdit(f)}
                        aria-label={`Redigera ${f.name}`}
                      >
                        Redigera
                      </button>
                      <button
                        type="button"
                        className="button button--danger button--small"
                        onClick={() => handleDelete(f)}
                        aria-label={`Ta bort ${f.name}`}
                      >
                        Ta bort
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
};

interface MetadataFieldFormProps {
  field?: ElectoralMetadataFieldListItem;
  onSave: (name: string, type: FieldType, importCode: string | null) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

function MetadataFieldForm({
  field,
  onSave,
  onCancel,
  isSaving
}: MetadataFieldFormProps): JSX.Element {
  const [name, setName] = useState(field?.name ?? "");
  const [importCode, setImportCode] = useState(field?.importCode ?? "");
  const [type, setType] = useState<FieldType>(field?.type ?? "Text");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(name, type, importCode.trim() || null);
  };

  return (
    <form onSubmit={handleSubmit} className="metadata-field-form">
      <div className="metadata-field-form__row">
        <label htmlFor="metadata-field-name">
          Namn
        </label>
        <input
          id="metadata-field-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Namn"
          required
        />
      </div>
      <div className="metadata-field-form__row">
        <label htmlFor="metadata-field-import-code">
          Importkod (CSV-kolumn)
        </label>
        <input
          id="metadata-field-import-code"
          type="text"
          value={importCode}
          onChange={(e) => setImportCode(e.target.value)}
          aria-label="Importkod för CSV-import"
          placeholder="t.ex. EU24, RD22"
        />
        <span className="metadata-field-form__hint">
          Valfritt. Om satt accepterar CSV-import både detta kolumnnamn och fältets namn.
        </span>
      </div>
      <div className="metadata-field-form__row">
        <label htmlFor="metadata-field-type">
          Typ
        </label>
        <select
          id="metadata-field-type"
          value={type}
          onChange={(e) => setType(e.target.value as FieldType)}
          aria-label="Typ"
        >
          <option value="Decimal">Decimal</option>
          <option value="Text">Text</option>
        </select>
      </div>
      <div className="metadata-field-form__actions">
        <button
          type="submit"
          className="button button--primary"
          disabled={isSaving}
        >
          {isSaving ? "Sparar..." : "Spara"}
        </button>
        <button
          type="button"
          className="button button--secondary"
          onClick={onCancel}
        >
          Avbryt
        </button>
      </div>
    </form>
  );
}
